import { Link, NavLink, Outlet, useNavigate } from 'react-router';
import { signOut } from 'aws-amplify/auth';
import {
  BarChart3,
  Bell,
  Briefcase,
  Cake,
  LogOut,
  Send,
  Sparkles,
  Users,
} from 'lucide-react';
import { cn } from '@/util/misc';
import { useAuth } from '@/context/auth';
import { ROLE_LABEL } from '@/util/permissions';
import { useNotifications } from '@/hooks/queries/contracting/useContractingComms';
import type { Permission } from '@/util/permissions';

/**
 * Back-office shell for the Contracting module.
 *
 * Nav items are filtered by permission, which reproduces the design's
 * per-role navigation: Contracting loses Campañas and Cumpleaños, Broker
 * Support and Onboarding lose Carriers and Reportes, and only Admin sees
 * everything.
 */

interface NavItem {
  to: string;
  label: string;
  icon: React.ElementType;
  permission: Permission;
}

const NAV: NavItem[] = [
  { to: '/agentes', label: 'Agentes', icon: Users, permission: 'agents.read' },
  { to: '/carriers', label: 'Carriers', icon: Briefcase, permission: 'carriers.read' },
  { to: '/reportes', label: 'Reportes', icon: BarChart3, permission: 'reports.read' },
  { to: '/notificaciones', label: 'Notificaciones', icon: Bell, permission: 'notifications.read' },
  { to: '/campanas', label: 'Campañas', icon: Send, permission: 'campaigns.manage' },
  { to: '/cumpleanos', label: 'Cumpleaños', icon: Cake, permission: 'birthdays.manage' },
];

export default function DashboardLayout() {
  const { user, role, can } = useAuth();
  const navigate = useNavigate();
  const { data: notifications } = useNotifications();

  const unread = notifications?.unread ?? 0;
  const items = NAV.filter((item) => can(item.permission));

  const initials = (user?.name || user?.email || '?')
    .split(' ')
    .slice(0, 2)
    .map((word) => word[0])
    .join('')
    .toUpperCase();

  const handleSignOut = async () => {
    await signOut();
    navigate('/sign-in', { replace: true });
  };

  return (
    <div className="bg-muted/40 flex h-screen flex-col">
      <header className="bg-navy-900 flex h-14 shrink-0 items-center justify-between px-4 text-white">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-br from-sky-500 to-blue-600 text-base font-extrabold shadow-lg">
            R
          </div>
          <div className="leading-tight">
            <div className="text-sm font-extrabold tracking-tight">
              Roca <span className="text-sky-400">IG</span>
            </div>
            <div className="text-[9px] font-semibold tracking-widest text-white/50">
              CONTRACTING
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-3">
          <NavLink
            to="/notificaciones"
            className="relative rounded-lg bg-white/10 p-2 transition-colors hover:bg-white/20"
            aria-label={`Notificaciones${unread ? `, ${unread} sin leer` : ''}`}
          >
            <Bell className="h-4 w-4" />
            {unread > 0 && (
              <span className="border-navy-900 absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full border-2 bg-red-500 px-1 text-[10px] font-bold">
                {unread > 9 ? '9+' : unread}
              </span>
            )}
          </NavLink>

          <div className="flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-xs font-bold">
              {initials}
            </div>
            <div className="hidden leading-tight sm:block">
              <div className="text-xs font-bold">{user?.name || user?.email}</div>
              <div className="text-[11px] text-white/50">
                {role ? ROLE_LABEL[role] : '—'}
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleSignOut}
            className="rounded-lg bg-white/10 p-2 transition-colors hover:bg-white/20"
            aria-label="Cerrar sesión"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </header>

      <div className="flex flex-1 overflow-hidden">
        <nav className="bg-navy-950 flex w-56 shrink-0 flex-col overflow-y-auto p-3">
          <div className="px-3 pt-1 pb-2.5 text-[10px] font-bold tracking-widest text-white/40">
            MÓDULO CONTRACTING
          </div>

          {items.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  cn(
                    'relative mb-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors',
                    isActive
                      ? 'bg-white/10 font-bold text-white'
                      : 'font-medium text-white/60 hover:bg-white/5 hover:text-white/90',
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    {isActive && (
                      <span className="absolute top-2 bottom-2 left-0 w-[3px] rounded-full bg-sky-400" />
                    )}
                    <Icon
                      className={cn(
                        'h-4 w-4',
                        isActive ? 'text-sky-400' : 'text-white/55',
                      )}
                    />
                    {item.label}
                    {item.to === '/notificaciones' && unread > 0 && (
                      <span className="ml-auto flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold">
                        {unread}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}

          <div className="mt-auto pt-4">
            <div className="rounded-xl bg-white/[0.06] p-3">
              <div className="mb-1 flex items-center gap-1.5 text-[11px] font-bold text-sky-400">
                <Sparkles className="h-3 w-3" />
                Bridge de comunicación
              </div>
              <p className="text-[11px] leading-relaxed text-white/60">
                Visibilidad total del proceso de cada agente entre
                departamentos.
              </p>
            </div>
          </div>
        </nav>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
